'use client';

import {
  formatStructuredAddress,
  type StructuredDepartureAddress,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import type {
  MovingTypeIntakeView,
  MovingTypeSpecificationAttribute,
  MovingTypeSpecificationResponse,
} from '@/entities/visit-counsel/model/moving-type-intake.types';
import { MOVING_TYPE_CODE_FALLBACK_LABELS } from '@/entities/visit-counsel/model/moving-type-display';
import {
  resolveMovingTypeAttribute,
  type ResolvedMovingTypeAttributeType,
} from './resolveMovingTypeAttributeKind';
import {
  humanizeIntakeAttributeName,
  isPlaceholderDateLikeValue,
  isWellFormedOrderedDateRangeSlash,
  normalizeDateRangeSlashValue,
} from './intakeUiCopy';
import {
  ENUM_CODE_KOREAN_LABELS,
  humanizeAttrLabel,
  humanizeEnumValue,
  humanizeIntakeAttributeKey,
  looksLikeEnglishLabel,
  looksLikeRawCamelCase,
} from '@miso/counsel-schema';

const CHOICE_CUSTOM_CODE = '$CUSTOM';

/**
 * 라벨 사전과 humanize 규칙은 공유 패키지에 있다 — 프리뷰의 이사 유형 서브플로우가
 * 같은 사전을 보고 질문 제목을 만든다. 여기서 재노출해 기존 import 경로를 지킨다.
 */
export {
  humanizeAttrLabel,
  humanizeEnumValue,
  humanizeIntakeAttributeKey,
  looksLikeEnglishLabel,
  looksLikeRawCamelCase,
  ENUM_CODE_KOREAN_LABELS,
};

function parseStructured(value: string): StructuredDepartureAddress | null {
  if (!value?.trim()) return null;
  try {
    const j = JSON.parse(value) as StructuredDepartureAddress;
    if (typeof j?.roadAddress === 'string') return j;
    return null;
  } catch {
    return null;
  }
}

/** 인테이크 폼과 동일한 “값이 채워졌는지” 판정 (CHOICE_WITH_CUSTOM: 직접입력 시 본문 필수) */
export function isMovingTypeAttributeValueFilled(
  attr: MovingTypeSpecificationAttribute,
  resolved: ResolvedMovingTypeAttributeType,
  raw: string | undefined
): boolean {
  const val = raw ?? '';
  if (attr.optional && val.trim() === '') return true;
  switch (resolved.kind) {
    case 'STRING':
      return val.trim() !== '';
    case 'BOOLEAN':
      return val === 'true' || val === 'false';
    case 'NUMBER':
    case 'INT':
      return val !== '' && !Number.isNaN(Number(val));
    case 'DATE':
      if (isPlaceholderDateLikeValue(val)) return false;
      return val.trim() !== '';
    case 'ENUM':
      return val.trim() !== '';
    case 'DATE_RANGE': {
      if (!val.includes('/')) return false;
      const [a, b] = val.split('/');
      if (isPlaceholderDateLikeValue(String(a)) || isPlaceholderDateLikeValue(String(b))) {
        return false;
      }
      return isWellFormedOrderedDateRangeSlash(val.trim());
    }
    case 'TIME':
      try {
        const j = JSON.parse(val) as { hour?: number };
        return typeof j.hour === 'number';
      } catch {
        return false;
      }
    case 'STRUCTURED_DEPARTURE_ADDRESS': {
      const p = parseStructured(val);
      return Boolean(p?.roadAddress?.trim());
    }
    case 'CHOICE_WITH_CUSTOM': {
      try {
        const j = JSON.parse(val) as { choice?: string; text?: string };
        const c = j.choice != null ? String(j.choice).trim() : '';
        if (!c) return false;
        if (c === CHOICE_CUSTOM_CODE) {
          return typeof j.text === 'string' && j.text.trim() !== '';
        }
        return true;
      } catch {
        return false;
      }
    }
    default:
      return val.trim() !== '';
  }
}

/**
 * 인테이크 폼의 접힌 요약과 동일 규칙으로 사용자 표시 문자열 생성
 */
export function summarizeMovingTypeAttributeValue(
  resolved: ResolvedMovingTypeAttributeType,
  raw: string | undefined
): string {
  const val = raw ?? '';
  switch (resolved.kind) {
    case 'BOOLEAN':
      if (val !== 'true' && val !== 'false') return '미선택';
      return val === 'true' ? '예' : '아니오';
    case 'ENUM':
      return resolved.enumDisplayNames?.[val] ?? ENUM_CODE_KOREAN_LABELS[val] ?? val;
    case 'CHOICE_WITH_CUSTOM': {
      try {
        const j = JSON.parse(val) as { choice?: string; text?: string };
        const c = j.choice ?? '';
        if (c === CHOICE_CUSTOM_CODE) {
          const t = j.text?.trim() ?? '';
          return t ? `직접입력: ${t.length > 36 ? `${t.slice(0, 36)}…` : t}` : '직접입력';
        }
        return resolved.enumDisplayNames?.[c] ?? c;
      } catch {
        return val || '미선택';
      }
    }
    case 'DATE_RANGE': {
      if (!val.includes('/')) return '미선택';
      const norm = normalizeDateRangeSlashValue(val.trim());
      const [a, b] = norm.split('/');
      const as = String(a ?? '').trim();
      const bs = String(b ?? '').trim();
      if (!as || !bs || isPlaceholderDateLikeValue(as) || isPlaceholderDateLikeValue(bs)) {
        return '미선택';
      }
      return `${as} ~ ${bs}`;
    }
    case 'TIME': {
      try {
        const j = JSON.parse(val) as { hour?: number; minute?: number };
        if (typeof j.hour !== 'number') return '미선택';
        const m = j.minute ?? 0;
        return `${String(j.hour).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      } catch {
        return val || '미선택';
      }
    }
    case 'STRUCTURED_DEPARTURE_ADDRESS': {
      const p = parseStructured(val);
      if (!p?.roadAddress?.trim()) return '미선택';
      const line = formatStructuredAddress(p);
      return line.length > 120 ? `${line.slice(0, 120)}…` : line;
    }
    case 'NUMBER':
    case 'INT':
    case 'DATE':
      if (isPlaceholderDateLikeValue(val)) return '미선택';
      return val;
    case 'STRING':
      return val.length > 48 ? `${val.slice(0, 48)}…` : val;
    default:
      return summarizeUnknownRawValue(val, resolved);
  }
}

/**
 * spec.attribute.type 이 알 수 없는 토큰으로 내려와 resolved.kind 가 'STRING' fallback 으로
 * 빠진 경우에도, raw 저장값이 사실은 TIME/CHOICE_WITH_CUSTOM/BOOLEAN/structured JSON 인
 * 케이스가 많다 — 그대로 노출하면 "delayReason {"choice":"OTHER"}" 처럼 사용자에게 raw JSON 이
 * 노출된다. 본 헬퍼는 default 분기에서 best-effort 로 사람이 읽기 좋은 문자열을 만든다.
 */
function summarizeUnknownRawValue(val: string, resolved: ResolvedMovingTypeAttributeType): string {
  if (!val) return '미선택';
  if (val === 'true') return '예';
  if (val === 'false') return '아니오';
  const trimmed = val.trim();
  if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) {
    return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    // TIME JSON
    if (typeof parsed.hour === 'number') {
      const h = parsed.hour as number;
      const m = (typeof parsed.minute === 'number' ? parsed.minute : 0) as number;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    // CHOICE_WITH_CUSTOM JSON
    if (typeof parsed.choice === 'string') {
      const c = parsed.choice as string;
      if (c === CHOICE_CUSTOM_CODE) {
        const t = typeof parsed.text === 'string' ? (parsed.text as string).trim() : '';
        return t ? `직접입력: ${t.length > 36 ? `${t.slice(0, 36)}…` : t}` : '직접입력';
      }
      return resolved.enumDisplayNames?.[c] ?? ENUM_CODE_KOREAN_LABELS[c] ?? c;
    }
    // STRUCTURED_DEPARTURE_ADDRESS JSON
    if (typeof parsed.roadAddress === 'string') {
      const line = formatStructuredAddress(parsed as unknown as StructuredDepartureAddress);
      return line.length > 120 ? `${line.slice(0, 120)}…` : line;
    }
  } catch {
    // fall through
  }
  return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
}

function shouldShowAttributeInReview(
  attr: MovingTypeSpecificationAttribute,
  resolved: ResolvedMovingTypeAttributeType,
  raw: string | undefined
): boolean {
  if (!isMovingTypeAttributeValueFilled(attr, resolved, raw)) return false;
  const summary = summarizeMovingTypeAttributeValue(resolved, raw);
  return summary !== '미선택' && summary.trim() !== '';
}

export type MovingTypeIntakeReviewBlock = {
  code: string;
  title: string;
  rows: { label: string; value: string }[];
};

/**
 * 선택된 이사 유형별로 저장된 속성(attributesByType)을 리뷰용 블록으로 만든다.
 */
export function buildMovingTypeIntakeReviewBlocks(
  intake: MovingTypeIntakeView | null | undefined,
  specs: MovingTypeSpecificationResponse[] | null | undefined
): MovingTypeIntakeReviewBlock[] {
  const selected = intake?.selectedTypes ?? [];
  const byType = intake?.attributesByType ?? {};
  const byCode = new Map((specs ?? []).map((s) => [s.code, s]));

  return selected.map((code) => {
    const spec = byCode.get(code);
    const title = spec?.name?.trim() || MOVING_TYPE_CODE_FALLBACK_LABELS[code] || code;
    const rows: { label: string; value: string }[] = [];

    if (spec) {
      for (const attr of spec.attributes) {
        const resolved = resolveMovingTypeAttribute(attr);
        const raw = byType[code]?.[attr.key];
        if (!shouldShowAttributeInReview(attr, resolved, raw)) continue;
        const value = summarizeMovingTypeAttributeValue(resolved, raw);
        const baseLabel = humanizeIntakeAttributeName(attr.name ?? '', resolved.kind);
        // attr.name 이 영어 camelCase("delayReason") 또는 영어 구문("Needs electricity") 으로
        // 내려오는 경우 한국어 key 사전 매핑을 우선 적용한다.
        const label =
          looksLikeRawCamelCase(baseLabel) || looksLikeEnglishLabel(baseLabel)
            ? humanizeIntakeAttributeKey(attr.key, baseLabel)
            : baseLabel;
        rows.push({ label, value });
      }
    } else {
      // spec 매칭 실패 fallback — raw JSON 이 그대로 노출되지 않도록 key/value 모두 humanize.
      const rawByKey = byType[code];
      if (rawByKey) {
        for (const [attrKey, val] of Object.entries(rawByKey)) {
          if (!String(val ?? '').trim()) continue;
          rows.push({
            label: humanizeIntakeAttributeKey(attrKey),
            value: summarizeUnknownRawValue(String(val), {
              kind: 'STRING',
            }),
          });
        }
      }
    }

    return { code, title, rows };
  });
}
